<template>
  <section class="hero dark" id="hero">
    <div class="container">
      <div class="row">
        <div class="col-lg-8">
          <h5>{{ t('Everything you need in just one subscription!') }}</h5>
          <h1>
            {{ t('Develop your') }}
            <div>
              <span class="teletype" data-event="teletype"></span>
              {{ t('Website') }}
            </div>
            {{ t('with Veximus.') }}
          </h1>
          <p>
            <mark>
              <font-awesome-icon class="me-1" :icon="['far', 'stopwatch']" />
              {{ t('Limited Time Offer: Free Web Hosting + Free Top-Level Domain +') }} <br />{{ t('Free SSL + Free CDN + Free Business Emails.') }}
            </mark>
          </p>
          <div class="timer" data-event="timer">
            <div data-type="days">
              <span>00</span>
              <label>{{ t('Days') }}</label>
            </div>
            <div data-type="hours">
              <span>00</span>
              <label>{{ t('Hours') }}</label>
            </div>
            <div data-type="minutes">
              <span>00</span>
              <label>{{ t('Minutes') }}</label>
            </div>
            <div data-type="seconds">
              <span>00</span>
              <label>{{ t('Seconds') }}</label>
            </div>
          </div>
        </div>
        <div class="col-12">
          <div class="devices">
            <div class="device phone">
              <img class="frame" src="images/ui/hero-phone-frame.png" alt="Phone Frame" />
              <img class="content" src="images/ui/hero-phone-content.jpg" alt="Phone Content" />
              <img class="shadow" src="images/ui/hero-phone-shadow.png" alt="Phone Shadow" />
            </div>
            <div class="device laptop">
              <img class="frame" src="images/ui/hero-laptop-frame.png" alt="Laptop Frame" />
              <img class="content" src="images/ui/hero-laptop-content.jpg" alt="Laptop Content" />
              <img class="shadow" src="images/ui/hero-laptop-shadow.png" alt="Laptop Shadow" />
            </div>
            <div class="device tablet">
              <img class="frame" src="images/ui/hero-tablet-frame.png" alt="Tablet Frame" />
              <img class="content" src="images/ui/hero-tablet-content.jpg" alt="Tablet Content" />
              <img class="shadow" src="images/ui/hero-tablet-shadow.png" alt="Tablet Shadow" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

.hero {
  background: linear-gradient(90deg, $primary, #00a0ff);
  color: #fff;
  padding-bottom: 0;
  padding-top: 10rem;
  position: relative;

  &::before {
    background: url('/images/ui/hero-bg.png') no-repeat top;
    background-size: cover;
    bottom: 0;
    content: '';
    left: 0;
    opacity: 0.5;
    pointer-events: none;
    position: absolute;
    right: 0;
    top: 0;
  }

  h5 {
    color: #fff;
  }

  h1 {
    color: #fff;
    font-size: 3.5rem;
    line-height: 3.75rem;
    margin-bottom: 1.5rem;
    position: relative;
    z-index: 5;

    .teletype {
      color: $secondary;

      &::after {
        animation: flicker 0.75s infinite;
        content: '|';
        font-weight: 300;
        margin-left: 0.25rem;
      }

      @media (max-width: 991.98px) {
        display: block;
      }
    }

    @media (max-width: 575.98px) {
      font-size: 2.5rem;
      line-height: 2.5rem;
    }
  }

  p {
    margin-bottom: 1.5rem;
    position: relative;
    z-index: 5;

    mark {
      background: #097efb;
      color: #fff;
      padding: 0.25rem 0;

      @media (max-width: 991.98px) {
        background: none;
      }

      br {
        @media (max-width: 575.98px) {
          display: none;
        }
      }
    }
  }

  .timer {
    margin-bottom: 2rem;
    position: relative;
    z-index: 5;

    > div {
      align-items: center;
      display: inline-flex;
      flex-direction: column;
      margin-right: 1rem;

      > span {
        align-items: center;
        background: #fff;
        border-radius: 1.65rem;
        box-shadow: 0 0.5rem 2rem rgb(0 0 0 / 25%);
        color: $primary;
        display: flex;
        font-family: 'Rubik', sans-serif;
        font-size: 2.5rem;
        font-weight: 300;
        height: 70px;
        justify-content: center;
        letter-spacing: -0.1rem;
        margin-bottom: 0.5rem;
        position: relative;
        width: 70px;

        @media (max-width: 575.98px) {
          border-radius: 1.15rem;
          font-size: 1.5rem;
          height: 45px;
          width: 45px;
        }
      }

      > label {
        background: #222;
        border-radius: 0.25rem;
        display: block;
        font-size: 0.85rem;
        font-weight: 500;
        padding: 0 0.25rem;
        z-index: 5;
      }

      &:not(:last-child) {
        > span {
          &::after {
            color: #fff;
            content: ':';
            position: absolute;
            right: -0.75rem;
            z-index: 5;
          }
        }
      }
    }
  }

  .devices {
    align-items: center;
    display: flex;
    justify-content: center;
    margin-bottom: 5rem;
    margin-top: -15rem;
    transform: rotate(-30deg);

    .device {
      position: relative;
      transition: 0.5s ease-in-out;

      .frame {
        position: relative;
        z-index: 2;
      }

      .content {
        position: absolute;
        z-index: 1;
      }

      .shadow {
        box-shadow: none !important;
        max-width: initial;
        position: absolute;
      }

      &.phone {
        max-width: 9.5vw;

        .content {
          border-radius: 10%;
          left: 3%;
          width: calc(100% - 5%);
        }

        .shadow {
          left: -8%;
          top: -3.5%;
          width: 160%;
        }
      }

      &.laptop {
        max-width: 43vw;

        .content {
          left: 10%;
          top: 5%;
          width: calc(100% - 20%);
        }

        .shadow {
          left: -8%;
          top: -10%;
          width: 109%;
        }
      }

      &.tablet {
        max-width: 26vw;

        .frame {
          position: relative;
          z-index: 2;
        }

        .content {
          border-radius: 10%;
          left: 0;
          top: 0;
        }

        .shadow {
          left: 0;
          top: 0;
          width: 113%;
        }
      }
    }

    @media (max-width: 991.98px) {
      margin-top: -5rem;
      margin-bottom: 10rem;
    }
  }

  @keyframes flicker {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
}
</style>

<script>
import { useI18n } from 'vue-i18n';

export default {
  data() {
    return {
      teletypeTimeouts: []
    };
  },

  methods: {
    typeString(target, string, pointer, delay, callback) {
      target.innerHTML += string[pointer];
      if (pointer < string.length - 1) {
        this.teletypeTimeouts.push(
          setTimeout(() => {
            this.typeString(target, string, pointer + 1, delay, callback);
          }, delay)
        );
        return;
      }

      callback();
    },

    deleteString(target, delay, callback) {
      var length = target.innerHTML.length;

      target.innerHTML = target.innerHTML.substr(0, length - 1);
      if (length > 1) {
        this.teletypeTimeouts.push(
          setTimeout(() => {
            this.deleteString(target, delay, callback);
          }, delay)
        );
        return;
      }

      callback();
    },

    teletype(target, options) {
      var component = this,
        settings = _.extend(
          {
            delay: 100,
            pause: 3000,
            text: []
          },
          options
        );

      (function loop(target, index) {
        component.typeString(target, settings.text[index], 0, settings.delay, () => {
          component.teletypeTimeouts.push(
            setTimeout(() => {
              component.deleteString(target, settings.delay, () => {
                loop(target, (index + 1) % settings.text.length);
              });
            }, settings.pause)
          );
        });
      })(target, 0);
    },

    countdownTimer(target, options) {
      var settings = _.extend(
        {
          date: new Date(),
          interval: 1000
        },
        options
      );

      var timer = setInterval(() => {
        var now = new Date().getTime(),
          date = settings.date.getTime(),
          distance = date - now;

        if (distance < 0) {
          clearInterval(timer);
          return;
        }

        target.querySelector('[data-type="days"] span').innerHTML = String(Math.floor(distance / (1000 * 60 * 60 * 24))).padStart(2, '0');
        target.querySelector('[data-type="hours"] span').innerHTML = String(Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))).padStart(2, '0');
        target.querySelector('[data-type="minutes"] span').innerHTML = String(Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60))).padStart(2, '0');
        target.querySelector('[data-type="seconds"] span').innerHTML = String(Math.floor((distance % (1000 * 60)) / 1000)).padStart(2, '0');
      }, settings.interval);
    }
  },

  watch: {
    '$i18n.global.locale.value'(currentLocale, previousLocale) {
      var node = document.querySelector('[data-event="teletype"]');

      node.innerHTML = '';
      this.teletypeTimeouts.forEach((timeout) => {
        clearTimeout(timeout);
      });

      this.teletype(node, {
        text: [this.t('E-commerce'), this.t('Blog'), this.t('Portfolio'), this.t('Educational'), this.t('Showcase')]
      });
    }
  },

  mounted() {
    // Teletype
    this.teletype(document.querySelector('[data-event="teletype"]'), {
      text: [this.t('E-commerce'), this.t('Blog'), this.t('Portfolio'), this.t('Educational'), this.t('Showcase')]
    });

    // Countdown Timer
    var timer = new Date(),
      date = timer.getDate(),
      day = timer.getDay(),
      offset = (1 + 7 - day) % 7;

    timer.setDate(offset ? date + offset : date + 7);
    timer.setHours(0, 0, 0);

    this.countdownTimer(document.querySelector('[data-event="timer"]'), {
      date: timer
    });
  },

  setup() {
    const { t } = useI18n({
      messages: {
        'en-MY': {
          'Develop your': 'Develope your',
          'E-commerce': 'E-commerce',
          'Everything you need in just one subscription!': 'Everything you need in just one subscription!',
          'Free SSL + Free CDN + Free Business Emails.': 'Free SSL + Free CDN + Free Business Emails.',
          'Limited Time Offer: Free Web Hosting + Free Top-Level Domain +': 'Limited Time Offer: Free Web Hosting + Free Top-Level Domain +',
          'with Veximus.': 'with Veximus.',
          Blog: 'Blog',
          Days: 'Days',
          Educational: 'Educational',
          Hours: 'Hours',
          Minutes: 'Minutes',
          Portfolio: 'Portfolio',
          Seconds: 'Seconds',
          Showcase: 'Showcase',
          Website: 'Website'
        },
        'zh-MY': {
          'Develop your': '无论您想开发',
          'E-commerce': '电子商务',
          'Everything you need in just one subscription!': '仅仅需要一项订阅，即可满足您所有的需求！',
          'Free SSL + Free CDN + Free Business Emails.': '免费SSL + 免费CDN + 免费商务电子邮箱。',
          'Limited Time Offer: Free Web Hosting + Free Top-Level Domain +': '限时优惠：免费网络托管 + 免费顶级域名 +',
          'with Veximus.': '微星都能满足您。',
          Blog: '部落格',
          Days: '天数',
          Educational: '教育性',
          Hours: '小时',
          Minutes: '分钟',
          Portfolio: '作品集',
          Seconds: '秒钟',
          Showcase: '展示集',
          Website: '网站'
        },
        'ms-MY': {
          'Develop your': 'Binakan',
          'E-commerce': 'E-dagang',
          'Everything you need in just one subscription!': 'Semua yang anda perlukan hanya dalam satu pelan langganan!',
          'Free SSL + Free CDN + Free Business Emails.': 'SSL Percuma + CDN Percuma + E-mel Business Percuma.',
          'Limited Time Offer: Free Web Hosting + Free Top-Level Domain +': 'Tawaran Masa Terhad: Hosting Web Percuma + Domain Peringkat Atasan Percuma +',
          'with Veximus.': 'Dengan Veximus.',
          Blog: 'Blog',
          Days: 'Hari',
          Educational: 'Pendidikan',
          Hours: 'Jam',
          Minutes: 'Minit',
          Portfolio: 'Portfolio',
          Seconds: 'Saat',
          Showcase: 'Pameran',
          Website: 'Website'
        }
      }
    });

    return { t };
  }
};
</script>
